import React, { useState, useEffect } from 'react';
import { ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { images, navItems } from '../data/mockData';
import Navbar from './Navbar';

const HERO_BG = 'https://customer-assets.emergentagent.com/job_energy-healing-react/artifacts/cclw6kvw_ht-bg1a.jpg';

const FooterButterflyLogo = () => {
  const [imgLoaded, setImgLoaded] = useState(true);

  if (imgLoaded) {
    return (
      <img
        src={images.logo}
        alt="A Healing Touch"
        className="h-10 w-auto"
        onError={() => setImgLoaded(false)}
      />
    );
  }

  return (
    <svg className="h-10 w-auto" viewBox="0 0 80 70" fill="none">
      <path d="M40 35 C30 15, 5 5, 10 30 C12 40, 25 42, 40 35" fill="#C9A84C" opacity="0.8"/>
      <path d="M40 35 C50 15, 75 5, 70 30 C68 40, 55 42, 40 35" fill="#C9A84C" opacity="0.8"/>
      <path d="M40 35 C32 45, 15 55, 20 40 C22 36, 30 38, 40 35" fill="#7B4F9E" opacity="0.7"/>
      <path d="M40 35 C48 45, 65 55, 60 40 C58 36, 50 38, 40 35" fill="#7B4F9E" opacity="0.7"/>
      <line x1="40" y1="30" x2="40" y2="60" stroke="#4A154B" strokeWidth="1.5"/>
      <line x1="40" y1="30" x2="35" y2="20" stroke="#4A154B" strokeWidth="1"/>
      <line x1="40" y1="30" x2="45" y2="20" stroke="#4A154B" strokeWidth="1"/>
    </svg>
  );
};

const PageLayout = ({ scriptLabel, heading, children }) => {
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 400);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <>
      <Navbar />

      {/* Page Header */}
      <section
        className="relative overflow-hidden"
        style={{ paddingTop: '80px' }}
      >
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url(${HERO_BG})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        />
        <div className="relative z-10 max-w-6xl mx-auto px-4 py-14 md:py-20 text-center">
          <p
            className="font-seaweed text-[#C9A84C] text-3xl md:text-4xl mb-2"
            style={{ textShadow: '0 2px 8px rgba(74, 21, 75, 0.7), 0 0 20px rgba(0, 0, 0, 0.3)' }}
          >
            {scriptLabel}
          </p>
          <h1
            className="font-playfair text-white text-3xl md:text-5xl lg:text-6xl font-bold leading-tight"
            style={{ textShadow: '0 2px 10px rgba(74, 21, 75, 0.8), 0 0 30px rgba(0, 0, 0, 0.4)' }}
          >
            {heading}
          </h1>
        </div>
        <div
          className="h-1.5 relative z-10"
          style={{
            background: 'linear-gradient(to right, #8B5EA5, #C9A84C, #8B5EA5)',
          }}
        />
      </section>

      {/* Page Content */}
      <main>{children}</main>

      {/* Footer */}
      <footer className="py-8 px-4 bg-white border-t border-gray-100">
        <div className="max-w-6xl mx-auto">
          <div className="hidden md:grid grid-cols-[1fr_auto_1fr] items-center gap-6">
            <div className="flex items-center justify-end gap-6">
              {navItems.left.map((item) => (
                <Link
                  key={item.label}
                  to={item.href}
                  className="text-[#5B2C6F] hover:text-[#C9A84C] transition-colors duration-300 text-sm font-open-sans tracking-[0.12em] uppercase"
                >
                  {item.label}
                </Link>
              ))}
            </div>
            <Link to="/">
              <FooterButterflyLogo />
            </Link>
            <div className="flex items-center justify-start gap-6">
              {navItems.right.map((item) => (
                <Link
                  key={item.label}
                  to={item.href}
                  className="text-[#5B2C6F] hover:text-[#C9A84C] transition-colors duration-300 text-sm font-open-sans tracking-[0.12em] uppercase"
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
          <div className="md:hidden flex flex-wrap items-center justify-center gap-4">
            <Link to="/" className="w-full flex justify-center mb-4">
              <FooterButterflyLogo />
            </Link>
            {[...navItems.left, ...navItems.right].map((item) => (
              <Link
                key={item.label}
                to={item.href}
                className="text-[#5B2C6F] hover:text-[#C9A84C] transition-colors duration-300 text-xs font-open-sans tracking-[0.12em] uppercase"
              >
                {item.label}
              </Link>
            ))}
          </div>
          <p className="text-center text-[#999] text-xs mt-6 font-open-sans">
            © {new Date().getFullYear()} A Healing Touch Pain Release. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Scroll to Top */}
      <button
        onClick={scrollToTop}
        className={`fixed bottom-6 right-6 z-50 w-12 h-12 bg-[#C9A84C] text-white rounded-full shadow-lg flex items-center justify-center hover:bg-[#B89740] transition-colors duration-300 ${
          showScrollTop ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
        }`}
        aria-label="Scroll to top"
      >
        <ChevronUp size={24} />
      </button>
    </>
  );
};

export default PageLayout;
